import React, {
  forwardRef,
  useEffect,
  useState,
  useImperativeHandle,
  useRef,
} from 'react';
import ReactQuill, { Quill } from 'react-quill';
import { renderToString } from 'react-dom/server';
import quillEmoji from 'quill-emoji';
import 'react-quill/dist/quill.snow.css';
import { ButtonClose } from '@lk-gtcom/ecomlab-components';
import { ReactComponent as Smile } from './img/bx-smile.svg'
import { ReactComponent as Load } from './img/bx-load.svg'
import { ReactComponent as Send } from './img/bx-send.svg'
import EmojiPicker from 'emoji-picker-react';
import { DownloadComponent } from '../DownloadComponent/DownloadComponent';
import { FileItemComponent } from '../FileItemComponent/FileItemComponent';
import './Editor.scss';
import ImageViewer from '../ImageViewer/ImageViewer';
import _ from 'lodash';
const parse = require('html-react-parser');

const { EmojiBlot, ShortNameEmoji, ToolbarEmoji, TextAreaEmoji } = quillEmoji;

Quill.register(
  {
    'formats/emoji': EmojiBlot,
    'modules/emoji-shortname': ShortNameEmoji,
    'modules/emoji-toolbar': ToolbarEmoji,
    'modules/emoji-textarea': TextAreaEmoji,
  },
  true
);

export const editorFormats = [
  'size',
  'bold',
  'italic',
  'underline',
  'blockquote',
  'color',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'video',
  'emoji',
];

export const editorModules = {
  toolbar: [
    [[], [], [], { font: [] }, [], [], [], { size: [] }],
    [],
    [],
    [],
    [],
    [],
    [],
    [
      'link',
      'image',
      'video',
      { list: 'ordered' },
      { list: 'bullet' },
      { indent: '-1' },
      { indent: '+1' },
    ],
    [],
    [],
    [
      'bold',
      'italic',
      'underline',
      'blockquote',
      'code-block',
      { 'color': ['#e60000', '#9933ff', '#00ff00'] },
      'background',
    ],
    ['emoji'],
  ],
  'emoji-toolbar': true,
  'emoji-textarea': true,
  'emoji-shortname': true,
  'keyboard': {
    'bindings': {
      'listAutoformat': undefined, // Отключаем автоформатирование списков
    }
  }
};

// ВАЖНО: эти объекты созданы один раз на уровне модуля (не внутри компонента),
// чтобы react-quill получал одну и ту же ссылку на modules/formats при каждом
// ре-рендере. Если передавать новый объект на каждый рендер (например, создавая
// его внутри функции компонента), react-quill будет считать конфигурацию
// изменившейся и полностью реинициализировать редактор — из-за этого курсор
// сбрасывался/прыгал назад при каждом нажатии клавиши, включая пробел.
const plainTextFormats = [];

const plainTextModules = {
  toolbar: false,
  keyboard: {
    bindings: {
      listAutoformat: undefined,
      bold: undefined,
      italic: undefined,
      underline: undefined,
      strike: undefined,
      blockquote: undefined,
      'code-block': undefined,
      header: undefined,
      indent: undefined,
      'list': undefined,
      'script': undefined,
      'direction': undefined,
      'align': undefined,
      'color': undefined,
      'background': undefined,
      'font': undefined,
      'size': undefined,
      'link': undefined,
      'image': undefined,
      'video': undefined,
      'clean': undefined,
    }
  }
};

const Editor = forwardRef(
  (
    {
      placeholder = '',
      description = '',
      saveOnBtn = false,
      setDescription = false,
      title = 'Добавьте описание',
      comment = false,
      editValue = false,
      disabled = false,
      filesdata,
      setFiles,
      setFilesData,
      authorEmail,
      isEditComment,
      hideBtn,
      hideEdit,
      setIsChanges,
      fetchDeleteFiles,
      eventId,
      type,
      fetchGetFiles,
      setSaveFile,
      comment_id,
      saveFile,
      message_id,
      isAuthor,
      messageData,
      sendOnEnter = false,
      isFile = true,
      onBlur = () => { },
      fetchComments,
      currentMessageAnswer,
      plainTextOnly = false,
    },
    editorRef
  ) => {

    const [uploadProgress, setUploadProgress] = useState({})
    const [isModalUploadProgress, setIsModalUploadProgress] = useState(false)
    const [value, setValue] = useState(editValue ? editValue : '');
    const [previusDesc, setPreviusDesc] = useState('');
    const [text, setText] = useState('');
    const [theme, setTheme] = useState('snow');
    const [isEdit, setIsEdit] = useState(
      !!editValue || hideEdit ? true : false
    );
    const [showImg, setShowImg] = useState('');
    const refProgress = useRef(null)
    const quillRef = useRef(null);

    // ВАЖНО (фикс прыгающего курсора):
    // Раньше ReactQuill был полностью "контролируемым" — value={value} + onChange,
    // который тут же обновлял тот же самый value. При каждом нажатии клавиши
    // (особенно пробела, из-за нормализации HTML) react-quill видел, что новый
    // value отличается от текущего содержимого DOM, и делал editor.setContents(...),
    // что полностью пересоздаёт содержимое и сбрасывает позицию курсора.
    //
    // Теперь редактор неконтролируемый: используем defaultValue (задаётся один раз
    // при монтировании) и просто читаем значение через onChange в state, не скармливая
    // его обратно в ReactQuill на каждый keystroke. Когда контент нужно подставить
    // программно снаружи (reset(), смена description, открытие на редактирование
    // с новым editValue) — форсируем полный remount через смену key (resetKey).
    const [resetKey, setResetKey] = useState(0);

    // Не создаём новый объект/массив на каждый рендер — берём стабильные
    // ссылки, объявленные на уровне модуля (см. editorFormats/editorModules
    // и plainTextFormats/plainTextModules выше).
    const getEditorFormats = () => {
      return plainTextOnly ? plainTextFormats : editorFormats;
    };

    const getEditorModules = () => {
      return plainTextOnly ? plainTextModules : editorModules;
    };

    // Следим за изменениями disabled
    useEffect(() => {
      // Если disabled становится true, выходим из режима редактирования
      if (disabled && isEdit) {
        setIsEdit(false);
      } else {
        setIsEdit(true)
      }
    }, [disabled,]);

    useImperativeHandle(editorRef, () => ({
      reset() {
        setValue('');
        setText('');
        setPreviusDesc('');
        // форсируем remount ReactQuill, чтобы очистить его внутреннее содержимое
        // (т.к. теперь используется defaultValue, а не controlled value)
        setResetKey((k) => k + 1);
      },
    }));

    const stringToHtml = (str) => {
      return parse(str, {
        replace: (domNode) => {
          if (domNode.name === 'img') {
            return (
              <img
                src={domNode.attribs.src}
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImg(domNode.attribs.src);
                }}
              />
            );
          }
        },
      });
    };

    const onSaveDesc = (html) => {
      if (setDescription && html)
        setDescription(renderToString(html) ? renderToString(html) : '');
    };

    const onSaveHandler = (value) => {
      // Если поле disabled, не обновляем значение
      if (disabled) return;

      let processedValue = value;

      if (plainTextOnly) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = value;
        let plainText = tempDiv.textContent || tempDiv.innerText || '';
        processedValue = plainText;
        setValue(plainText);
        setText(plainText);
      } else {
        setValue(value);
        const html = stringToHtml(value);
        setText(html);
      }

      if (saveOnBtn) return;
      onSaveDesc(plainTextOnly ? processedValue : stringToHtml(value));
    };

    const handleSaveMessage = () => {
      if (disabled) return;
      if (hasFiles || value?.replace(/<[^>]*>/g, '').trim()) {
        setTimeout(() => {
          if (!hideEdit) {
            setIsEdit(false);
          }
        }, 1000);
        setIsSaveFiles(true);
        setPreviusDesc(value);
        if (saveOnBtn) onSaveDesc(text);
        if (fetchComments) {
          fetchComments(currentMessageAnswer?.comment_data, value)
        }
      }
    };

    useEffect(() => {
      if ((description?.length > 0) & !isEdit) {
        setText(stringToHtml(description));
        setValue(description);
      }
    }, [description]);

    useEffect(() => {
      if (!previusDesc) setPreviusDesc(description ? description : 'none');
    }, [description, isEdit]);

    const [isEmoji, setIsEmoji] = useState(false);

    useEffect(() => {
      const hideMenu = (e) => {
        let el = e.target;
        if (!el.closest('.redactor-wrapper__emoji-box')) {
          setIsEmoji(false);
        }
      };
      document.addEventListener('click', hideMenu);

      return () => {
        document.addEventListener('click', hideMenu);
      };
    }, []);

    const [isSaveFiles, setIsSaveFiles] = useState(false);

    const hasFiles = (filesdata && filesdata.length > 0) || (saveFile && saveFile.length > 0);


    // Обработчик нажатия клавиш для редактора
    const handleKeyDown = (e) => {
      // Получаем текущий quill редактор
      const quill = quillRef.current?.getEditor();

      // Если включена отправка по Enter и нажат Enter без Ctrl/Shift/Cmd - отправляем сообщение
      if (sendOnEnter && e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleSaveMessage();
        return false;
      }

      // Если нажат Ctrl+Enter или Cmd+Enter - добавляем перенос строки
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (quill) {
          // Вставляем перенос строки
          quill.insertText(quill.getSelection()?.index || 0, '\n');
        }
        return false;
      }

      // Если нажат Shift+Enter - добавляем перенос строки (стандартное поведение Quill)
      if (e.key === 'Enter' && e.shiftKey) {
        // Разрешаем стандартное поведение Quill - он сам добавит <br> или новый <p>
        return true;
      }
    };

    return (
      <div
        onBlur={onBlur}
        onClick={(e) => {
          if (setIsChanges) {
            setIsChanges(true);
          }
        }}
      >
        {showImg && (
          <ImageViewer
            isOpen={showImg}
            images={showImg}
            onClose={(e) => setShowImg(false)}
          />
        )}

        <div
          className={
            disabled ? 'editor-component_disabled' : 'editor-component'
          }
          style={{
            flexDirection: !hideBtn ? 'row' : '',
          }}
        >
          <div
            className="redactor-wrapper"
            // style={{ width: !hideBtn ? 'calc(100% - 96px)' : '' }}
          >
            {title && <div className="redactor-wrapper__title">{title}</div>}
            <div className="redactor-wrapper__field">
              {isEdit ? (
                <>
                  <div
                    className="text-content"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      if (!hideEdit) {
                        setIsEdit(true);
                      }
                    }}
                  >
                    <ReactQuill
                      // key меняется только когда контент нужно подставить
                      // программно (сброс, повторное открытие с editValue);
                      // на обычный ввод текста (в т.ч. пробел) key не меняется,
                      // поэтому Quill не пересоздаётся и курсор не прыгает.
                      key={`quill-${resetKey}`}
                      ref={quillRef}
                      disabled={disabled}
                      tabIndex="0"
                      defaultValue={value}
                      onChange={onSaveHandler}
                      onKeyDown={handleKeyDown}
                      placeholder={placeholder}
                      bounds={'.redactor-wrapper'}
                      formats={getEditorFormats()}
                      modules={getEditorModules()}
                      theme={plainTextOnly ? 'bubble' : theme}
                    ></ReactQuill>
                  </div>
                </>
              ) : !comment ? (
                <div
                  className="text-content"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    if (!disabled) {
                      if (!hideEdit) {
                        setIsEdit(true);
                      }
                    }
                  }}
                >
                  <p
                    className="text"
                    onClick={(e) => {
                      if (e?.target?.localName === 'a') {
                        e.stopPropagation();
                      }
                    }}
                  >
                    {description ? (
                      plainTextOnly
                        ? description
                        : stringToHtml(description)
                    ) : placeholder}
                  </p>
                </div>
              ) : (
                <div
                  className="comment__input-field"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) {
                      if (!hideEdit) {
                        setIsEdit(true);
                      }
                    }
                  }}
                >
                  <input
                    placeholder="Оставьте комментарий"
                    disabled={disabled}
                    onClick={(e) => {
                      if (setIsChanges) {
                        setIsChanges(true);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (sendOnEnter && e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                        handleSaveMessage();
                      }
                    }}
                  />
                </div>
              )}

              {filesdata?.length > 0 && (
                <div className="redactor-wrapper__files">
                  {filesdata?.map(
                    ({ created_at, file_size_kb, new_filename, url, file_id }, ind) => {

                      return (
                        <FileItemComponent
                          file_id={file_id}
                          comment_id={comment_id}
                          type={type}
                          eventId={eventId}
                          fetchDeleteFiles={fetchDeleteFiles}
                          fetchGetFiles={fetchGetFiles}
                          isEdit={isEdit}
                          created_at={created_at}
                          file_size_kb={file_size_kb}
                          new_filename={new_filename}
                          url={url}
                          authorEmail={authorEmail}
                          filesdata={filesdata}
                          setFilesData={(e) => setFilesData(e)}
                          positionDrop={'top'}
                          saveFile={saveFile}
                          setSaveFile={setSaveFile}
                          message_id={message_id}
                          messageData={messageData}
                        />
                      );
                    }
                  )}
                </div>
              )}

              {saveFile?.length > 0 && (
                <div className="redactor-wrapper__files">
                  {saveFile?.map(
                    ({ created_at, file_size_kb, new_filename, url, file_id }, ind) => {

                      return (
                        <FileItemComponent
                          file_id={file_id}
                          ind={ind}
                          comment_id={comment_id}
                          type={type}
                          eventId={eventId}
                          fetchDeleteFiles={fetchDeleteFiles}
                          fetchGetFiles={fetchGetFiles}
                          isEdit={isEdit}
                          created_at={created_at}
                          file_size_kb={file_size_kb}
                          new_filename={new_filename}
                          url={url}
                          authorEmail={authorEmail}
                          filesdata={saveFile}
                          setFilesData={(e) => setFilesData(e)}
                          positionDrop={'top'}
                          saveFile={saveFile}
                          setSaveFile={setSaveFile}
                          message_id={message_id}
                          isAuthor={isAuthor}
                          messageData={messageData}
                        />
                      );
                    }
                  )}
                </div>
              )}
            </div>
          </div>

          {(isEdit || isEditComment) ? (
            <div className="btn-group">
              {isEdit && (
                <div className='redactor-wrapper__upload-box'>
                  <div className="redactor-wrapper__toolbar-bottom">
                    {/* <progress ref={refProgress} id="progressBar" value={0} max={100} style={{ width: '100%', height: '20px' }}></progress> */}

                    {!plainTextOnly && (
                      <div className="redactor-wrapper__emoji-box">
                        <Smile
                          className='redactor-wrapper__icon'
                          onClick={(e) => setIsEmoji(!isEmoji)}
                        />

                        {isEmoji && (
                          <div className="redactor-wrapper__emoji-drop">
                            <EmojiPicker
                              style={{ width: '100%' }}
                              width={100}
                              height={300}
                              //reactionsDefaultOpen={true}
                              searchDisabled={true}
                              // skinTonesDisabled
                              onEmojiClick={(e) => {
                                const { emoji } = e;
                                const quill = quillRef.current?.getEditor();
                                if (!quill) return;

                                // Вставляем эмодзи прямо в Quill-инстанс в текущую
                                // позицию курсора (или в конец, если фокуса не было) —
                                // редактор неконтролируемый, поэтому setValue сам по
                                // себе больше не меняет видимое содержимое.
                                const range =
                                  quill.getSelection(true) || {
                                    index: quill.getLength(),
                                    length: 0,
                                  };
                                quill.insertText(range.index, emoji, 'user');
                                quill.setSelection(
                                  range.index + emoji.length,
                                  0,
                                  'user'
                                );
                                quill.focus();

                                // Синхронизируем React state с реальным содержимым
                                // редактора, чтобы value/text/description оставались
                                // консистентными для остальной логики компонента
                                // (handleSaveMessage, saveOnBtn и т.д.), не вызывая
                                // remount самого Quill.
                                const html = quill.root.innerHTML;
                                setValue(html);
                                const parsedHtml = stringToHtml(html);
                                setText(parsedHtml);
                                if (!saveOnBtn) {
                                  onSaveDesc(parsedHtml);
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}


                    {(filesdata && setFiles && isFile && setSaveFile) && (
                      <button className="redactor-wrapper__btn-load">
                        <Load
                          className='redactor-wrapper__icon'
                        />
                        {
                          // (email == authorEmail) &&
                          <DownloadComponent
                            isAuthor={isAuthor}
                            type={type}
                            isSaveFiles={isSaveFiles}
                            setIsSaveFiles={(e) => setIsSaveFiles(e)}
                            filesdata={filesdata}
                            comment_id={comment_id}
                            // authorEmail={email}
                            refProgress={refProgress}
                            setFilesArr={(e) => setFiles(e)}
                            setFilesData={(e) => setFilesData(e)}
                            setSaveFile={setSaveFile}
                            uploadProgress={uploadProgress}
                            setUploadProgress={setUploadProgress}
                            setIsModalUploadProgress={setIsModalUploadProgress}
                          />
                        }
                      </button>
                    )}

                    {!hideBtn && (
                      <button
                        className="redactor-wrapper__btn-send"
                        key={'add-btn'}
                        onClick={handleSaveMessage}
                      >
                        <Send
                          className='redactor-wrapper__icon'
                        />
                      </button>
                    )}
                  </div>
                </div>

              )}
            </div>
          ) : null}
        </div>

        {
          ((Object.keys(uploadProgress).length > 0) && isModalUploadProgress) &&
          <div className={'progress'}>
            <ButtonClose
              xs
              onClick={(e) => {
                setIsModalUploadProgress(false)
              }}
            />
            <div className={'progress__content'}>
              {Object.entries(uploadProgress).map(([fileName, progress]) => (
                <div key={fileName} className={'progress__item'}>
                  <span className='progress__label'>{fileName}</span>
                  <progress className='progress__bar' value={progress} max="100">{progress}%</progress>
                  <span className='progress__percent'>{progress}%</span>
                </div>
              ))}
            </div>
          </div>
        }
      </div>
    );
  }
);

export { Editor };
